/**
 * <site-header>: desktop mega menu, mobile drawer (2 levels), search bar toggle, countdown tiles.
 * Markup contract: see sections/header.liquid (data-mega-toggle, data-mega-panel, data-menu-open, data-menu-close,
 * data-mobile-menu, data-level-open, data-level-back, data-search-open, data-search-bar, data-search-close).
 */
class SiteHeader extends HTMLElement {
  #abort = new AbortController();
  #countdownTimer = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.toggles = [...this.querySelectorAll('[data-mega-toggle]')];
    this.menu = this.querySelector('[data-mobile-menu]');
    this.searchBar = this.querySelector('[data-search-bar]');
    this.searchToggle = this.querySelector('[data-search-open]');
    this.backdrop = this.querySelector('[data-search-backdrop]');

    for (const toggle of this.toggles) {
      toggle.addEventListener('click', () => this.#toggleMega(toggle), { signal });
    }

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node) || this.contains(event.target)) return;
      this.#closeMega();
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.#openMega()) {
        const toggle = this.#openMega();
        this.#closeMega();
        toggle.focus();
      } else if (this.hasAttribute('data-searching')) {
        this.closeSearch();
      }
    }, { signal });

    this.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && !this.contains(next)) this.#closeMega();
    }, { signal });

    this.#initMobileMenu(signal);
    this.#initSearch(signal);
    this.#initCountdowns();
  }

  disconnectedCallback() {
    this.#abort.abort();
    clearInterval(this.#countdownTimer);
  }

  /* ---------- Mega menu ---------- */
  #openMega() {
    return this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true') || null;
  }

  #closeMega() {
    for (const toggle of this.toggles) {
      toggle.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
    }
  }

  #toggleMega(toggle) {
    const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
    this.#closeMega();
    if (wasOpen) return;
    this.closeSearch();
    toggle.setAttribute('aria-expanded', 'true');
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panel.hidden = false;
  }

  /* ---------- Mobile drawer ---------- */
  #initMobileMenu(signal) {
    if (!this.menu) return;
    const opener = this.querySelector('[data-menu-open]');
    const levels = [...this.menu.querySelectorAll('[data-level]')];

    const showLevel = (name, focusTarget) => {
      for (const level of levels) level.hidden = level.dataset.level !== name;
      const active = levels.find((level) => level.dataset.level === name);
      const target = focusTarget || (active && (active.querySelector('[data-level-back]') || active.querySelector('a, button, input')));
      if (target instanceof HTMLElement) target.focus();
    };

    opener?.addEventListener('click', () => {
      showLevel('root');
      this.menu.showModal();
      document.body.classList.add('has-modal-open');
      opener.setAttribute('aria-expanded', 'true');
    }, { signal });

    const close = () => {
      if (this.menu.open) this.menu.close();
    };

    this.menu.addEventListener('close', () => {
      document.body.classList.remove('has-modal-open');
      opener?.setAttribute('aria-expanded', 'false');
      opener?.focus();
    }, { signal });

    this.menu.addEventListener('click', (event) => {
      if (event.target === this.menu) return close();
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-menu-close]')) return close();
      const open = target.closest('[data-level-open]');
      if (open) {
        this.lastLevelTrigger = open;
        return showLevel(open.dataset.levelOpen);
      }
      if (target.closest('[data-level-back]')) return showLevel('root', this.lastLevelTrigger);
      if (target.closest('a[href]')) close();
    }, { signal });

    // Close the drawer if the viewport grows into the desktop layout.
    window.matchMedia('(min-width: 1100px)').addEventListener('change', (event) => {
      if (event.matches) close();
    }, { signal });
  }

  /* ---------- Search ---------- */
  #initSearch(signal) {
    if (!this.searchBar || !this.searchToggle) return;

    this.searchToggle.addEventListener('click', () => this.openSearch(), { signal });
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.closeSearch(), { signal });
    this.backdrop?.addEventListener('click', () => this.closeSearch(), { signal });
    document.addEventListener('search:open', () => this.openSearch(), { signal });
    document.addEventListener('search:close', () => this.closeSearch(), { signal });
  }

  openSearch() {
    if (!this.searchBar) return;
    this.#closeMega();
    this.searchBar.hidden = false;
    if (this.backdrop) this.backdrop.hidden = false;
    this.setAttribute('data-searching', '');
    this.searchToggle?.setAttribute('aria-expanded', 'true');
    this.searchBar.querySelector('[data-search-input]')?.focus();
    document.dispatchEvent(new CustomEvent('search:opened'));
  }

  closeSearch() {
    if (!this.searchBar || this.searchBar.hidden) return;
    this.searchBar.hidden = true;
    if (this.backdrop) this.backdrop.hidden = true;
    this.removeAttribute('data-searching');
    this.searchToggle?.setAttribute('aria-expanded', 'false');
    this.searchToggle?.focus();
    document.dispatchEvent(new CustomEvent('search:closed'));
  }

  /* ---------- Countdown tiles ---------- */
  #initCountdowns() {
    const nodes = [...this.querySelectorAll('[data-countdown-end]')];
    if (nodes.length === 0) return;
    const strings = (window.RM && window.RM.config.strings) || {};
    const pad = (value) => String(value).padStart(2, '0');
    const tick = () => {
      const now = Date.now();
      for (const node of nodes) {
        const end = Date.parse(node.dataset.countdownEnd);
        if (Number.isNaN(end)) continue;
        const diff = Math.max(0, end - now);
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        node.textContent = `${pad(days)}${strings.days || 'D'} ${pad(hours)}${strings.hours || 'H'} ${pad(minutes)}${strings.minutes || 'M'}`;
      }
    };
    tick();
    this.#countdownTimer = setInterval(tick, 30000);
  }
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
